import { useState, useCallback, useRef } from 'react'
import { readFileAsDataURL, processImageToRatio, getCroppedImage, calculateInitialCrop } from '../utils/imageProcessing'
import { CROP_SETTINGS, PROCESSING_DELAYS } from '../AgentHeadshotUploader.constants'

/**
 * Custom hook for managing image cropping functionality
 * @param {boolean} enableCrop - Whether cropping is enabled
 * @returns {Object} - Crop state and handlers
 */
export const useImageCrop = (enableCrop = true) => {
  const [imageSrc, setImageSrc] = useState(null)
  const [originalImageSrc, setOriginalImageSrc] = useState(null)
  // Calculate initial crop using percentage units
  // Width: 80% crop selector, centered on 100% visible image
  const initialCropWidth = CROP_SETTINGS.INITIAL_WIDTH_PERCENT
  const initialCropHeight = initialCropWidth / CROP_SETTINGS.ASPECT_RATIO
  const [crop, setCrop] = useState({
    aspect: CROP_SETTINGS.ASPECT_RATIO,
    width: initialCropWidth,
    height: initialCropHeight,
    x: (100 - initialCropWidth) / 2, // Center horizontally
    y: (100 - initialCropHeight) / 2, // Center vertically
    unit: CROP_SETTINGS.PERCENTAGE_UNIT,
  })
  const [isProcessing, setIsProcessing] = useState(false)
  const [isProcessed, setIsProcessed] = useState(false)
  const [error, setError] = useState(null)
  const isInitialCropSet = useRef(false)

  const imageRef = useRef(null)

  /**
   * Handles file selection and processing
   */
  const handleFileSelect = useCallback(async files => {
    // Validate input
    if (!files || files.length === 0) {
      return
    }

    const file = files[0]

    // Additional validation
    if (!file) {
      return
    }

    // Reset states
    setError(null)
    setIsProcessing(true)
    setIsProcessed(false)

    try {
      // Read the file as data URL
      const dataUrl = await readFileAsDataURL(file)
      setOriginalImageSrc(dataUrl)
      setImageSrc(dataUrl)

      // Skip canvas processing - use original image directly
      // This ensures crop works directly with the actual image without padding
      setIsProcessed(true)
      setIsProcessing(false)
    } catch (err) {
      console.error('Failed to read image:', err)
      // Use the error message from the Error object if available
      const errorMessage = err instanceof Error ? err.message : 'Failed to read image file'
      setError(errorMessage)
      setIsProcessing(false)
      setIsProcessed(false)
    }
  }, [])

  /**
   * Handles image load event to set initial crop
   */
  const handleImageLoaded = useCallback(image => {
    imageRef.current = image

    try {
      const initialCrop = calculateInitialCrop(image)
      // console.log('Initial crop from calculateInitialCrop:', initialCrop)
      isInitialCropSet.current = true
      setCrop(initialCrop)
      // Reset flag after a short delay to allow ReactCrop to initialize
      setTimeout(() => {
        isInitialCropSet.current = false
      }, 100)
    } catch (err) {
      console.error('Failed to set initial crop:', err)
      setError('Failed to initialize crop')
    }
  }, [])

  /**
   * Handles crop change event
   */
  const handleCropChange = useCallback(newCrop => {
    if (newCrop.width && newCrop.height) {
      // Skip adjustment during initial crop setup to preserve exact calculated values
      if (isInitialCropSet.current) {
        // console.log('Skipping adjustment during initial crop setup')
        return
      }

      // Enforce minimum crop size for user-initiated changes
      const adjustedCrop = {
        ...newCrop,
        width: Math.max(newCrop.width, CROP_SETTINGS.MIN_WIDTH),
        height: Math.max(newCrop.height, CROP_SETTINGS.MIN_HEIGHT),
      }
      // console.log("adjustedCrop", adjustedCrop)
      setCrop(adjustedCrop)
    }
  }, [])

  /**
   * Converts a data URL to a blob
   */
  const dataURLtoBlob = useCallback(dataUrl => {
    return new Promise((resolve, reject) => {
      try {
        const arr = dataUrl.split(',')
        const mime = arr[0].match(/:(.*?);/)[1]
        const bstr = atob(arr[1])
        let n = bstr.length
        const u8arr = new Uint8Array(n)
        while (n--) {
          u8arr[n] = bstr.charCodeAt(n)
        }
        resolve(new Blob([u8arr], { type: mime }))
      } catch (err) {
        reject(new Error('Failed to convert data URL to blob'))
      }
    })
  }, [])

  /**
   * Gets the cropped image blob (or full image if crop disabled)
   */
  const getCroppedImageBlob = useCallback(async () => {
    // If crop is disabled, just convert the imageSrc to a blob
    if (!enableCrop) {
      if (!imageSrc) {
        throw new Error('No image selected')
      }
      return await dataURLtoBlob(imageSrc)
    }

    // If crop is enabled, use the crop functionality
    if (!imageRef.current || !crop.width || !crop.height) {
      throw new Error('No image or crop data available')
    }

    return await getCroppedImage(imageRef.current, crop)
  }, [crop, enableCrop, imageSrc, dataURLtoBlob])

  /**
   * Resets the crop state
   */
  const resetCrop = useCallback(() => {
    const resetCropWidth = CROP_SETTINGS.INITIAL_WIDTH_PERCENT
    const resetCropHeight = resetCropWidth / CROP_SETTINGS.ASPECT_RATIO
    setImageSrc(null)
    setOriginalImageSrc(null)
    setCrop({
      aspect: CROP_SETTINGS.ASPECT_RATIO,
      width: resetCropWidth,
      height: resetCropHeight,
      x: (100 - resetCropWidth) / 2, // Center horizontally
      y: (100 - resetCropHeight) / 2, // Center vertically
      unit: CROP_SETTINGS.PERCENTAGE_UNIT,
    })
    setIsProcessing(false)
    setIsProcessed(false)
    setError(null)
    imageRef.current = null
    isInitialCropSet.current = false
  }, [])

  return {
    // State
    imageSrc,
    originalImageSrc,
    crop,
    isProcessing,
    isProcessed,
    error,
    imageRef,

    // Handlers
    handleFileSelect,
    handleImageLoaded,
    handleCropChange,
    getCroppedImageBlob,
    resetCrop,
  }
}
